import { useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import { CirclePlus } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Input from '@/components/form/Input';
import SpinnerButton from '@/components/common/SpinnerButton';
import Error from '@/components/common/Error';

import {
  usePostOfferingCategory,
  useOfferingCategories
} from '@/data/offeringCategory';

const formSchema = z.object({
  label: z.string().min(1, { message: 'Label is required' })
});

const AddOfferingCategoryDialog = () => {
  const [open, setIsOpen] = useState(false);
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const postOfferingCategory = usePostOfferingCategory();
  const offeringCategories = useOfferingCategories();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      label: ''
    }
  });

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setApiError('');
      const offeringCategory = await postOfferingCategory(values);
      await offeringCategories.mutate(
        (currentOfferingCategories) => [
          ...currentOfferingCategories,
          offeringCategory
        ],
        false
      );
      setIsOpen(false);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <CirclePlus />
          Add Offering Category
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Offering Category</DialogTitle>
          <DialogDescription>Add a new offering category.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Input name="label" label="Label" autoComplete="off" />
            {apiError && <Error error={apiError} />}
            <DialogFooter>
              <SpinnerButton loading={submitting}>
                Add Offering Category
              </SpinnerButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddOfferingCategoryDialog;
